import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';
import { MarkdownView } from './MarkdownView';

/**
 * PoliciesTab — Parent-facing read-only view of the org's published
 * policies. Card grid grouped by category, click to open a detail
 * modal with full text + print/PDF button.
 */
const CATEGORY_ORDER = ['Commercial', 'Safeguarding', 'Welfare', 'Health & Safety', 'Governance'];

const categoryEmoji = (c) => ({
  Commercial: '💰',
  Safeguarding: '🛡️',
  Welfare: '🤍',
  'Health & Safety': '🩺',
  Governance: '📜',
})[c] || '📄';

export function PoliciesTab() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [viewing, setViewing] = useState(null);
  // Ack state: `{ policies: [{slug, state, current_version, ...}], pending_count }`
  const [ackStatus, setAckStatus] = useState(null);
  const [acking, setAcking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [data, ack] = await Promise.all([
        api.listPublishedPolicies(),
        api.getPolicyAcknowledgmentStatus().catch(() => null),
      ]);
      setRows(Array.isArray(data) ? data : []);
      setAckStatus(ack || null);
    } catch (err) {
      toast.error(err?.message || 'Failed to load policies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Map slug → state so cards can badge themselves without re-scanning.
  const stateBySlug = useMemo(() => {
    const m = {};
    for (const p of ackStatus?.policies || []) m[p.slug] = p;
    return m;
  }, [ackStatus]);

  const acknowledgeOne = async (slug) => {
    setAcking(true);
    try {
      await api.acknowledgePolicies([slug]);
      // Refetch just the ack status so we don't disturb `viewing`.
      const ack = await api.getPolicyAcknowledgmentStatus();
      setAckStatus(ack);
      toast.success('Thanks — acknowledgment recorded.');
    } catch (err) {
      toast.error(err?.message || 'Acknowledgment failed');
    } finally {
      setAcking(false);
    }
  };

  const acknowledgeAll = async () => {
    setAcking(true);
    try {
      const res = await api.acknowledgePolicies('all');
      const ack = await api.getPolicyAcknowledgmentStatus();
      setAckStatus(ack);
      toast.success(`Acknowledged ${res.count} polic${res.count === 1 ? 'y' : 'ies'}.`);
    } catch (err) {
      toast.error(err?.message || 'Acknowledgment failed');
    } finally {
      setAcking(false);
    }
  };

  const grouped = useMemo(() => {
    const g = {};
    for (const r of rows) {
      const c = r.category || 'Other';
      (g[c] = g[c] || []).push(r);
    }
    return g;
  }, [rows]);

  const orderedCategories = useMemo(() => {
    const known = CATEGORY_ORDER.filter((c) => grouped[c]);
    const extras = Object.keys(grouped).filter((c) => !CATEGORY_ORDER.includes(c));
    return [...known, ...extras];
  }, [grouped]);

  return (
    <div data-testid="parent-policies-tab">
      <div style={{ marginBottom: '1rem' }}>
        <h2 style={{ margin: 0 }}>📚 Policies</h2>
        <p style={{ color: 'hsl(var(--muted-foreground))', margin: '0.25rem 0 0', fontSize: '0.9rem' }}>
          Our current policies. Tap any card to read the full text. Use the print button inside to save a PDF for your records.
        </p>
      </div>

      {/* Sign-off banner — surfaces only when there are unacknowledged
          or stale policies. Prompts the parent to acknowledge one-click. */}
      {ackStatus && ackStatus.pending_count > 0 && (
        <div
          data-testid="parent-ack-banner"
          style={{
            display: 'flex',
            gap: '0.75rem',
            alignItems: 'center',
            padding: '0.75rem 1rem',
            border: '1px solid hsl(35 80% 55%)',
            background: 'hsl(45 95% 96%)',
            color: 'hsl(30 70% 25%)',
            borderRadius: 'var(--radius)',
            marginBottom: '1rem',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontWeight: 600 }}>
              📝 {ackStatus.pending_count} polic{ackStatus.pending_count === 1 ? 'y needs' : 'ies need'} your acknowledgment
            </div>
            <div style={{ fontSize: '0.8rem', marginTop: '0.15rem' }}>
              Please confirm you've read the current version of each policy. This is used as our Ofsted evidence trail.
            </div>
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={acknowledgeAll}
            disabled={acking}
            data-testid="parent-ack-all"
          >
            {acking ? 'Acknowledging…' : `✓ Acknowledge all ${ackStatus.pending_count}`}
          </button>
        </div>
      )}

      {loading ? (
        <div className="loading" data-testid="parent-policies-loading">Loading policies…</div>
      ) : rows.length === 0 ? (
        <div className="empty-state" data-testid="parent-policies-empty" style={{ padding: '2rem 1rem' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>📄</div>
          <h3 style={{ margin: '0 0 0.25rem' }}>No policies published yet</h3>
          <p style={{ margin: 0, color: 'hsl(var(--muted-foreground))', fontSize: '0.9rem' }}>
            The setting hasn't published policies to this portal yet. Please contact the office for a copy.
          </p>
        </div>
      ) : (
        orderedCategories.map((cat) => (
          <div key={cat} style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'hsl(var(--muted-foreground))', margin: '0 0 0.5rem' }}>
              {categoryEmoji(cat)} {cat}
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '0.75rem' }}>
              {grouped[cat].map((p) => {
                const stateInfo = stateBySlug[p.slug];
                const state = stateInfo?.state || 'not_ack';
                const stateStyle =
                  state === 'current'
                    ? { label: '✓ Acknowledged', bg: 'hsl(140 60% 92%)', fg: 'hsl(140 45% 26%)' }
                    : state === 'stale'
                    ? { label: '⚠ Updated — re-acknowledge', bg: 'hsl(35 90% 92%)', fg: 'hsl(30 80% 30%)' }
                    : { label: 'New — please read', bg: 'hsl(220 75% 94%)', fg: 'hsl(220 65% 30%)' };
                return (
                <button
                  type="button"
                  key={p._id || p.slug}
                  className="card"
                  onClick={() => setViewing(p)}
                  data-testid={`parent-policy-card-${p.slug}`}
                  style={{
                    textAlign: 'left',
                    padding: '0.9rem 1rem',
                    cursor: 'pointer',
                    border: '1px solid hsl(var(--border))',
                    background: 'hsl(var(--card))',
                    borderRadius: 'var(--radius)',
                    transition: 'transform .1s ease, box-shadow .1s ease',
                  }}
                  onMouseDown={(e) => (e.currentTarget.style.transform = 'translateY(1px)')}
                  onMouseUp={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
                  onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
                >
                  <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>{p.title}</div>
                  {p.ofsted_reference && p.ofsted_reference !== '—' && (
                    <div style={{ fontSize: '0.72rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.25rem' }}>
                      {p.ofsted_reference}
                    </div>
                  )}
                  <div style={{ marginTop: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.4rem' }}>
                    <span
                      className="badge"
                      data-testid={`parent-policy-state-${p.slug}`}
                      style={{
                        background: stateStyle.bg,
                        color: stateStyle.fg,
                        fontSize: '0.68rem',
                        padding: '0.12rem 0.5rem',
                        borderRadius: 999,
                        fontWeight: 600,
                      }}
                    >
                      {stateStyle.label}
                    </span>
                    <span style={{ fontSize: '0.72rem', color: 'hsl(var(--muted-foreground))' }}>
                      Tap to read →
                    </span>
                  </div>
                </button>
                );
              })}
            </div>
          </div>
        ))
      )}

      {viewing && (
        <div className="modal-overlay" onClick={() => setViewing(null)} data-testid="parent-policy-viewer">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 760, width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <div>
                <h2 style={{ margin: 0 }}>{viewing.title}</h2>
                <small style={{ color: 'hsl(var(--muted-foreground))' }}>
                  {viewing.category}{viewing.ofsted_reference && viewing.ofsted_reference !== '—' ? ` · ${viewing.ofsted_reference}` : ''}{viewing.version ? ` · v${viewing.version}` : ''}
                </small>
              </div>
              <button type="button" className="btn btn-ghost" onClick={() => setViewing(null)} data-testid="parent-policy-viewer-close">✕</button>
            </div>
            <div
              style={{
                maxHeight: '60vh',
                overflowY: 'auto',
                padding: '0.5rem 0.75rem',
                border: '1px solid hsl(var(--border))',
                borderRadius: 'var(--radius)',
                background: 'hsl(var(--muted) / 0.15)',
                marginBottom: '0.75rem',
              }}
              data-testid="parent-policy-body"
            >
              <MarkdownView source={viewing.body_markdown} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              <div style={{ fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))' }} data-testid="parent-policy-viewer-ack-state">
                {(() => {
                  const s = stateBySlug[viewing.slug];
                  if (!s || s.state === 'not_ack') return '📝 Not yet acknowledged';
                  if (s.state === 'stale') return `⚠ Acknowledged v${s.acknowledged_version} — updated to v${s.current_version}`;
                  return `✓ Acknowledged on ${new Date(s.acknowledged_at).toLocaleDateString()}`;
                })()}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {stateBySlug[viewing.slug]?.state !== 'current' && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => acknowledgeOne(viewing.slug)}
                    disabled={acking}
                    data-testid="parent-policy-ack"
                  >
                    {acking ? 'Saving…' : '✓ I have read and understood this policy'}
                  </button>
                )}
                <a
                  className="btn btn-ghost"
                  href={api.getPolicyPrintableUrl(viewing.slug)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="parent-policy-print"
                >
                  🖨 Print / Save as PDF
                </a>
                <button type="button" className="btn btn-ghost" onClick={() => setViewing(null)} data-testid="parent-policy-done">
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PoliciesTab;
